/* eslint-disable react-perf/jsx-no-new-function-as-prop */
/* eslint-disable react-perf/jsx-no-new-array-as-prop */
import { Container, LoadingOverlay, Slider } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import useUserStore from "@/modules/users/store";
import CreateConviteModal from "@/src/routes/responder-questionario-externo-page/components/CreateConviteModal";
import QuestionarioActions from "./QuestionarioActions";
import QuestionarioPages from "./QuestionarioPages";
import useGetPerguntaConvite from "../../hooks/useGetPerguntaConvite";
import useGetRespostaConvite from "../../hooks/useGetRespostaConvite";
import useQuestionarioStepper from "../../hooks/useQuestionarioStepper";

export type QuestionarioPage = (TGruposDiagnostico | TPergunta) & { type: "grupo" | "pergunta" };

type Properties = {
  grupos: TGruposDiagnostico[];
  questionario: TQuestionario;
  licenciado: TLicenciado | undefined;
  userId: string | undefined;
  empresaId: TEmpresa["id"];
  empresaRespondenteId: TEmpresa["id"];
};

const getPages = (grupos: TGruposDiagnostico[], perguntas: TPergunta[]) => {
  const perguntaByGrupoId = new Map<string, QuestionarioPage[]>();

  for (const p of perguntas) {
    const grupoId = p.grupo;
    const pergunta = perguntaByGrupoId.get(grupoId) || [];
    pergunta.push({ ...p, type: "pergunta" });
    perguntaByGrupoId.set(grupoId, pergunta);
  }

  const pages: QuestionarioPage[] = [];

  for (const grupo of grupos) {
    const perguntasInGrupo = perguntaByGrupoId.get(grupo.id) ?? [];
    pages.push({ ...grupo, type: "grupo" }, ...perguntasInGrupo);
  }

  return pages;
};

/* eslint-disable sonarjs/cognitive-complexity */
/**
 * QuestionarioContent
 * @param root0 - properties
 * @param root0.grupos - gruposIds
 * @param root0.questionario - questionario
 * @param root0.licenciado - licenciado
 * @param root0.userId - userId
 * @param root0.empresaId - empresaId
 * @param root0.empresaRespondenteId - empresaRespondenteId
 * @returns QuestionarioContent
 */
export function QuestionarioContent({
  grupos,
  questionario,
  licenciado,
  userId,
  empresaId,
  empresaRespondenteId
}: Properties) {
  const licenciadaId = useUserStore((state) => state.activeLicenciadaId);
  const [respostasQuestionario, setRespostasQuestionario] = useState<TResposta["perguntas"]>([]);
  const gruposIds = useMemo(() => grupos.map((grupo) => grupo.id), [grupos]);

  const { data: resposta } = useGetRespostaConvite({
    rootEmpresaId: empresaId,
    id: userId,
    idQuestionario: questionario?.id
  });

  const { data: perguntasFound } = useGetPerguntaConvite({ ids: gruposIds, activeEmpresaId: empresaId });
  const respostas = useMemo(
    () => resposta?.filter((item) => item.idEmpresa === empresaRespondenteId) ?? [],
    [resposta]
  );
  const perguntas = useMemo(() => perguntasFound?.sort((a, b) => a?.ordem - b?.ordem) ?? [], [perguntasFound]);
  const pages = useMemo(() => getPages(grupos, perguntas), [grupos, perguntas]);
  const respostaUser = useMemo(() => respostas.find((item) => item.idRespondente === userId), [respostas]);

  const controller = useQuestionarioStepper({
    questionario,
    pages,
    perguntas,
    respostas: respostasQuestionario,
    resposta: respostaUser,
    userId,
    empresaId,
    empresaRespondenteId,
    grupos
  });

  useEffect(() => {
    if (perguntas.length > 0) {
      const userRespostas = respostaUser?.perguntas ?? [];
      const respostasByPergunta = new Map(userRespostas.map((item) => [item.id, item.resposta]));

      setRespostasQuestionario(
        perguntas.map((item) => ({
          id: item.id,
          resposta: respostasByPergunta.get(item.id) ?? false,
          grupo: item.grupo
        }))
      );
    }
  }, [perguntas.length, respostaUser?.perguntas.length]);

  const handleChangeValue = (perguntaId: string, value: TRespostaPergunta["resposta"]) => {
    const perguntaFound = respostasQuestionario.find((item) => item.id === perguntaId);
    if (perguntaFound) {
      const perguntaMapped = { ...perguntaFound, resposta: value };
      setRespostasQuestionario((previous) => previous.map((item) => (item.id === perguntaId ? perguntaMapped : item)));
    }
  };
  const submitLabel = respostasQuestionario.some((respostaItem) => respostaItem.resposta) ? "Finalizar" : "Iniciar";

  return (
    <Container
      fluid
      style={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        padding: 0
      }}
    >
      <LoadingOverlay visible={controller.isSaving} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />

      {/* Slider fixo no topo */}
      <div style={{ flexShrink: 0, padding: "0 16px" }}>
        <Slider
          label={null}
          value={controller.step * controller.stepGap + controller.stepGap}
          step={controller.stepGap}
          styles={{ markLabel: { display: "none" } }}
        />
      </div>

      {/* Área principal que usa todo espaço disponível */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          padding: "16px",
          alignItems: "center",
          justifyContent: "flex-start"
        }}
      >
        {/* Container das perguntas e botões juntos */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%"
          }}
        >
          {/* Container das perguntas */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              width: "100%"
            }}
          >
            <QuestionarioPages
              onChangeResposta={handleChangeValue}
              controller={controller}
              pages={pages}
              licenciado={licenciado}
              respostas={respostasQuestionario}
              resposta={respostaUser}
              empresaId={empresaId}
            />
          </div>

          {/* Container dos botões logo após as perguntas */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              paddingTop: "24px"
            }}
          >
            <QuestionarioActions controller={controller} />
          </div>
        </div>
      </div>

      {controller.showConviteModal && (
        <CreateConviteModal
          submitLabel={submitLabel}
          questionario={questionario}
          empresaId={empresaId}
          licenciadaId={licenciadaId}
          empresaRespondenteId={empresaRespondenteId}
          opened={controller.showConviteModal}
          onClose={controller.handleCloseConviteModal}
        />
      )}
    </Container>
  );
}
